import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { PublicLayout, Section, Eyebrow } from "@/components/PublicLayout";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, Check, GraduationCap, Users, BookOpen, Mail, ShieldCheck, Loader2 } from "lucide-react";

const COUNTRIES = [
  ["CM", "Cameroon"], ["CD", "DR Congo"], ["CI", "Côte d'Ivoire"], ["SN", "Senegal"], ["NG", "Nigeria"], ["GH", "Ghana"],
  ["BJ", "Benin"], ["BF", "Burkina Faso"], ["TG", "Togo"], ["ML", "Mali"], ["NE", "Niger"], ["GA", "Gabon"],
  ["CG", "Congo"], ["TD", "Chad"], ["KE", "Kenya"], ["RW", "Rwanda"], ["UG", "Uganda"], ["TZ", "Tanzania"],
  ["ZM", "Zambia"], ["MW", "Malawi"], ["ZA", "South Africa"], ["MA", "Morocco"], ["DZ", "Algeria"], ["TN", "Tunisia"],
  ["ET", "Ethiopia"], ["AO", "Angola"], ["MZ", "Mozambique"], ["ZW", "Zimbabwe"], ["GN", "Guinea"], ["MG", "Madagascar"],
  ["FR", "France"], ["BE", "Belgium"], ["CH", "Switzerland"], ["GB", "United Kingdom"], ["IE", "Ireland"], ["US", "United States"],
  ["CA", "Canada"], ["DE", "Germany"], ["ES", "Spain"], ["IT", "Italy"], ["PT", "Portugal"], ["NL", "Netherlands"],
  ["SE", "Sweden"], ["NO", "Norway"], ["DK", "Denmark"], ["LU", "Luxembourg"], ["AE", "United Arab Emirates"], ["SA", "Saudi Arabia"],
  ["QA", "Qatar"], ["TR", "Turkey"], ["IN", "India"], ["CN", "China"], ["JP", "Japan"], ["BR", "Brazil"],
  ["MX", "Mexico"], ["AU", "Australia"], ["NZ", "New Zealand"], ["OT", "Other"],
];

const T = {
  en: {
    eyebrow: "Admissions · Application Centre",
    h1: "Begin your application",
    intro: "Online-first. First create your Application Centre account, then build your application at your own pace. The €97 fee is only requested when you take the mandatory assessment.",
    createAccount: "Create your Application Centre account",
    accountNote: "One account manages every application — including several children under the same family.",
    name: "Your full name", email: "Email address", password: "Create a password (min 6 characters)",
    createBtn: "Create account & continue", have: "Already have an account?", logIn: "Log in",
    verifyTitle: "Confirm your email", verifyLead: "We've sent a 6-digit code to",
    codePlaceholder: "6-digit code", verifyBtn: "Verify & continue", resend: "Resend the code",
    spam: "Don't see it? Check your spam or promotions folder and mark the email as \u201cnot spam\u201d so future Simo emails reach you.",
    who: "Who is this application for?", chooseProg: "Choose a programme", where: "Where will the student live and study?",
    whereNote: "Tuition follows the student's ordinary residence and primary study location — subject to admissions review.",
    residence: "Country of residence", study: "Primary study location",
    details: "Parent & child details", ownDetails: "Your details",
    guardian: "Parent / guardian full name", childName: "Child's full name", ownName: "Your full name", childAge: "Child's age",
    consent1: "I agree to the", privacy: "privacy", and: "and", safeguarding: "safeguarding", consent2: "terms",
    consentChild: ", and I am the parent or legal guardian of this child.", consentDot: ".",
    createApp: "Create my application", creating: "Creating…", back: "Back", cont: "Continue",
    noPay: "No payment now. The €97 fee is only requested when you choose to take the assessment.",
    types: [
      { v: "child", label: "For my child", note: "A parent account with one or more children" },
      { v: "adult", label: "For myself", note: "Higher-education applicant" },
      { v: "educator", label: "As an educator", note: "Teaching or partnership interest" },
    ],
  },
  fr: {
    eyebrow: "Admissions · Espace candidature",
    h1: "Commencez votre candidature",
    intro: "En ligne d'abord. Créez d'abord votre compte Espace candidature, puis constituez votre dossier à votre rythme. Les frais de 97 € ne sont demandés qu'au moment de l'évaluation obligatoire.",
    createAccount: "Créez votre compte Espace candidature",
    accountNote: "Un seul compte gère toutes les candidatures — y compris plusieurs enfants d'une même famille.",
    name: "Votre nom complet", email: "Adresse e-mail", password: "Créez un mot de passe (6 caractères min.)",
    createBtn: "Créer le compte et continuer", have: "Vous avez déjà un compte ?", logIn: "Se connecter",
    verifyTitle: "Confirmez votre e-mail", verifyLead: "Nous avons envoyé un code à 6 chiffres à",
    codePlaceholder: "Code à 6 chiffres", verifyBtn: "Vérifier et continuer", resend: "Renvoyer le code",
    spam: "Vous ne le voyez pas ? Vérifiez vos courriers indésirables (spam/promotions) et marquez l'e-mail comme « non indésirable » afin de bien recevoir nos futurs messages.",
    who: "Pour qui est cette candidature ?", chooseProg: "Choisissez un programme", where: "Où l'étudiant vivra-t-il et étudiera-t-il ?",
    whereNote: "Les frais suivent la résidence habituelle de l'étudiant et son principal lieu d'étude — sous réserve de l'examen des admissions.",
    residence: "Pays de résidence", study: "Principal lieu d'étude",
    details: "Coordonnées du parent et de l'enfant", ownDetails: "Vos coordonnées",
    guardian: "Nom complet du parent / responsable", childName: "Nom complet de l'enfant", ownName: "Votre nom complet", childAge: "Âge de l'enfant",
    consent1: "J'accepte les conditions de", privacy: "confidentialité", and: "et de", safeguarding: "protection", consent2: "",
    consentChild: ", et je suis le parent ou le représentant légal de cet enfant.", consentDot: ".",
    createApp: "Créer ma candidature", creating: "Création…", back: "Retour", cont: "Continuer",
    noPay: "Aucun paiement maintenant. Les frais de 97 € ne sont demandés que si vous choisissez de passer l'évaluation.",
    types: [
      { v: "child", label: "Pour mon enfant", note: "Un compte parent avec un ou plusieurs enfants" },
      { v: "adult", label: "Pour moi-même", note: "Candidat à l'enseignement supérieur" },
      { v: "educator", label: "En tant qu'éducateur", note: "Enseignement ou partenariat" },
    ],
  },
};
const TYPE_ICONS = { child: Users, adult: GraduationCap, educator: BookOpen };

export default function Apply() {
  const { user, register, verifyEmail, resendVerification } = useAuth();
  const { lang } = useI18n();
  const t = T[lang] || T.en;
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [progs, setProgs] = useState([]);
  const [step, setStep] = useState(0);
  const [acct, setAcct] = useState({ name: "", email: "", password: "" });
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    programme: params.get("programme") || "", applicant_type: "child",
    applicant_name: "", guardian_name: "", student_age: "",
    country_residence: "CM", study_location: "CM", consent: false,
  });
  useEffect(() => { api.get("/programmes").then((r) => setProgs(r.data)); }, []);
  useEffect(() => { if (params.get("programme")) setStep(1); }, [params]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const isChild = f.applicant_type === "child";
  const selectedProg = progs.find((p) => p.slug === f.programme);

  const authStage = !user ? "signup" : (user.email_verified === false ? "verify" : "ready");

  const doRegister = async () => {
    if (!acct.name || !acct.email || acct.password.length < 6) return;
    setBusy(true);
    try { await register({ name: acct.name, email: acct.email, password: acct.password, role: "applicant", language: lang }); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const doVerify = async () => {
    setBusy(true);
    try { await verifyEmail(code.trim()); toast.success(lang === "fr" ? "E-mail confirmé" : "Email confirmed"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const doResend = async () => {
    try { await resendVerification(); toast.success(lang === "fr" ? "Nouveau code envoyé" : "New code sent"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/applications", {
        programme: f.programme, applicant_type: f.applicant_type,
        applicant_name: f.applicant_name, guardian_name: isChild ? f.guardian_name : null,
        student_age: f.student_age ? parseInt(f.student_age) : null,
        country_residence: f.country_residence, study_location: f.study_location,
        language: lang, consent: f.consent, source: "apply",
      });
      toast.success(lang === "fr" ? "Candidature créée — bienvenue dans votre Espace candidature." : "Application created — welcome to your Application Centre.");
      nav(`/centre/${data.application_id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const canCreate = f.programme && f.applicant_name && (isChild ? f.guardian_name : true) && f.consent;

  // -------- Account gate (signup / verify) --------
  if (authStage !== "ready") {
    return (
      <PublicLayout>
        <Section className="py-16 md:py-24 max-w-xl">
          <Eyebrow>{t.eyebrow}</Eyebrow>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-ink">{t.h1}</h1>
          <p className="mt-4 text-ink/60">{t.intro}</p>

          {authStage === "signup" ? (
            <div className="mt-10 border border-border rounded-sm p-6 md:p-8 bg-paper" data-testid="apply-account">
              <div className="flex items-center gap-2 text-heraldic mb-1"><Mail className="h-4 w-4" /><span className="eyebrow">1 · {lang === "fr" ? "Compte" : "Account"}</span></div>
              <h2 className="font-serif text-2xl text-ink">{t.createAccount}</h2>
              <p className="text-sm text-ink/55 mt-1">{t.accountNote}</p>
              <div className="mt-5 space-y-3">
                <Input data-testid="signup-name" placeholder={t.name} value={acct.name} onChange={(e) => setAcct({ ...acct, name: e.target.value })} className="rounded-sm" />
                <Input data-testid="signup-email" type="email" placeholder={t.email} value={acct.email} onChange={(e) => setAcct({ ...acct, email: e.target.value })} className="rounded-sm" />
                <Input data-testid="signup-password" type="password" placeholder={t.password} value={acct.password} onChange={(e) => setAcct({ ...acct, password: e.target.value })} className="rounded-sm" />
              </div>
              <button data-testid="signup-submit" disabled={busy || !acct.name || !acct.email || acct.password.length < 6} onClick={doRegister}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-heraldic text-white px-7 py-3 rounded-sm hover:bg-heraldic-700 transition-colors disabled:opacity-40">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} {t.createBtn}
              </button>
              <p className="mt-4 text-sm text-ink/55">{t.have} <Link to="/login?next=/apply" data-testid="apply-login-link" className="text-heraldic font-medium">{t.logIn} →</Link></p>
            </div>
          ) : (
            <div className="mt-10 border border-border rounded-sm p-6 md:p-8 bg-paper" data-testid="apply-verify">
              <div className="flex items-center gap-2 text-heraldic mb-1"><ShieldCheck className="h-4 w-4" /><span className="eyebrow">2 · {lang === "fr" ? "Vérification" : "Verification"}</span></div>
              <h2 className="font-serif text-2xl text-ink">{t.verifyTitle}</h2>
              <p className="text-sm text-ink/60 mt-1">{t.verifyLead} <b>{user?.email}</b>.</p>
              <Input data-testid="verify-code" inputMode="numeric" maxLength={6} placeholder={t.codePlaceholder} value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="mt-5 rounded-sm text-center text-2xl tracking-[0.5em] font-mono" />
              <button data-testid="verify-submit" disabled={busy || code.length !== 6} onClick={doVerify}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-heraldic text-white px-7 py-3 rounded-sm hover:bg-heraldic-700 transition-colors disabled:opacity-40">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {t.verifyBtn}
              </button>
              <button data-testid="verify-resend" onClick={doResend} className="mt-3 text-sm text-heraldic hover:underline">{t.resend}</button>
              <p className="mt-4 text-xs text-ink/50 leading-relaxed border-t border-border pt-3">{t.spam}</p>
            </div>
          )}
        </Section>
      </PublicLayout>
    );
  }

  // -------- Application wizard --------
  return (
    <PublicLayout>
      <Section className="py-16 md:py-24 max-w-3xl">
        <Eyebrow>{t.eyebrow}</Eyebrow>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-ink">{t.h1}</h1>
        <p className="mt-4 text-ink/60">{t.intro}</p>

        <div className="mt-8 flex gap-2" data-testid="apply-progress">
          {[0, 1, 2, 3].map((i) => <div key={i} className={`h-1 flex-1 rounded-full ${i <= step ? "bg-heraldic" : "bg-border"}`} />)}
        </div>

        {step === 0 && (
          <div className="mt-10">
            <div className="eyebrow text-ink/40 mb-4">{t.who}</div>
            <div className="grid sm:grid-cols-3 gap-px bg-border border border-border">
              {t.types.map((ty) => {
                const Icon = TYPE_ICONS[ty.v];
                return (
                  <button key={ty.v} data-testid={`apply-type-${ty.v}`} onClick={() => { set("applicant_type", ty.v); setStep(1); }}
                    className={`bg-paper p-6 text-left hover:bg-secondary transition-colors ${f.applicant_type === ty.v ? "ring-2 ring-heraldic ring-inset" : ""}`}>
                    <Icon className="h-6 w-6 text-heraldic" />
                    <div className="mt-3 font-serif text-lg text-ink">{ty.label}</div>
                    <div className="text-xs text-ink/50 mt-1">{ty.note}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="mt-10 space-y-4">
            <div className="eyebrow text-ink/40">{t.chooseProg}</div>
            <Select value={f.programme} onValueChange={(v) => set("programme", v)}>
              <SelectTrigger data-testid="apply-programme" className="rounded-sm"><SelectValue placeholder={t.chooseProg} /></SelectTrigger>
              <SelectContent>{progs.map((p) => <SelectItem key={p.slug} value={p.slug}>{p[`name_${lang}`] || p.name_en}</SelectItem>)}</SelectContent>
            </Select>
            {selectedProg && <p className="text-sm text-ink/55">{selectedProg[`purpose_${lang}`] || selectedProg.purpose_en}</p>}
            <div className="flex justify-between pt-4">
              <button onClick={() => setStep(0)} className="text-sm text-ink/50">← {t.back}</button>
              <button data-testid="apply-next-1" disabled={!f.programme} onClick={() => setStep(2)} className="inline-flex items-center gap-2 bg-ink text-paper px-6 py-2.5 rounded-sm disabled:opacity-40">{t.cont} <ArrowRight className="h-4 w-4" /></button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="mt-10 space-y-4">
            <div className="eyebrow text-ink/40">{t.where}</div>
            <p className="text-xs text-ink/45">{t.whereNote}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="text-sm text-ink/60">{t.residence}</label>
                <Select value={f.country_residence} onValueChange={(v) => set("country_residence", v)}>
                  <SelectTrigger data-testid="apply-residence" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="text-sm text-ink/60">{t.study}</label>
                <Select value={f.study_location} onValueChange={(v) => set("study_location", v)}>
                  <SelectTrigger data-testid="apply-study" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-between pt-4">
              <button onClick={() => setStep(1)} className="text-sm text-ink/50">← {t.back}</button>
              <button data-testid="apply-next-2" onClick={() => setStep(3)} className="inline-flex items-center gap-2 bg-ink text-paper px-6 py-2.5 rounded-sm">{t.cont} <ArrowRight className="h-4 w-4" /></button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="mt-10 space-y-4">
            <div className="eyebrow text-ink/40">{isChild ? t.details : t.ownDetails}</div>
            {isChild && <Input data-testid="apply-guardian" placeholder={t.guardian} value={f.guardian_name} onChange={(e) => set("guardian_name", e.target.value)} className="rounded-sm" />}
            <Input data-testid="apply-applicant" placeholder={isChild ? t.childName : t.ownName} value={f.applicant_name} onChange={(e) => set("applicant_name", e.target.value)} className="rounded-sm" />
            {isChild && <Input data-testid="apply-age" type="number" placeholder={t.childAge} value={f.student_age} onChange={(e) => set("student_age", e.target.value)} className="rounded-sm" />}
            <label className="flex items-start gap-2 text-sm text-ink/60"><input data-testid="apply-consent" type="checkbox" checked={f.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1" /> {t.consent1} <Link to="/privacy" className="text-heraldic mx-1">{t.privacy}</Link> {t.and} <Link to="/safeguarding" className="text-heraldic mx-1">{t.safeguarding}</Link> {t.consent2}{isChild ? t.consentChild : t.consentDot}</label>
            <div className="flex justify-between pt-4">
              <button onClick={() => setStep(2)} className="text-sm text-ink/50">← {t.back}</button>
              <button data-testid="apply-create" disabled={!canCreate || busy} onClick={create} className="inline-flex items-center gap-2 bg-heraldic text-white px-7 py-3 rounded-sm hover:bg-heraldic-700 transition-colors disabled:opacity-40">{busy ? t.creating : t.createApp} <ArrowRight className="h-4 w-4" /></button>
            </div>
            <p className="text-xs text-ink/40 flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-heraldic" /> {t.noPay}</p>
          </div>
        )}
      </Section>
    </PublicLayout>
  );
}
